/**
 * MobileMenu Module - Following guidelines
 * Pure Tailwind, no inline styles, array mapping
 */

class MobileMenu {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SELECTORS: {
				button: '.ui-h-mb',
				menu: '.ui-h-mm',
				submenuToggle: '.ui-h-st',
				submenu: '.ui-h-sm',
				arrow: '.ui-h-a',
			},
			STATES: {
				menu: {
					open: ['block'],
					closed: ['hidden'],
				},
				submenu: {
					open: ['block'],
					closed: ['hidden'],
				},
				arrow: {
					open: ['rotate-180'],
					closed: ['rotate-0'],
				},
			},
		}

		// Get elements
		this.elements = this.getElements()

		// Initialize if elements exist
		if (this.elements.button && this.elements.menu) {
			this.init()
		}
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		const button = document.querySelector(SELECTORS.button)
		return {
			button,
			menu: document.querySelector(SELECTORS.menu),
			burgerLines: button ? button.querySelectorAll('svg path') : [],
		}
	}

	init() {
		// Bind events
		this.bindEvents()
		this.bindSubmenuEvents()
	}

	bindEvents() {
		// Toggle menu on button click
		this.elements.button.addEventListener('click', () => this.toggle())

		// Close menu when clicking outside
		document.addEventListener('click', e => {
			const { button, menu } = this.elements
			if (!button.contains(e.target) && !menu.contains(e.target)) {
				this.close()
			}
		})

		// Close on ESC key
		document.addEventListener('keydown', e => {
			if (e.key === 'Escape' && this.isOpen()) {
				this.close()
			}
		})
	}

	bindSubmenuEvents() {
		const { SELECTORS } = this.CONFIG
		const submenuToggles = document.querySelectorAll(SELECTORS.submenuToggle)

		submenuToggles.forEach(toggle => {
			toggle.addEventListener('click', e => {
				e.preventDefault()
				e.stopPropagation()

				const targetId = toggle.getAttribute('data-target')
				const submenu = document.getElementById(targetId)
				const arrow = toggle.querySelector(SELECTORS.arrow)

				if (submenu) {
					this.toggleSubmenu(submenu, arrow)
				}
			})
		})
	}

	toggleSubmenu(submenu, arrow) {
		const { STATES } = this.CONFIG
		const isOpen = !submenu.classList.contains('hidden')

		// Close other open submenus first
		document.querySelectorAll(this.CONFIG.SELECTORS.submenu).forEach(otherSubmenu => {
			if (otherSubmenu !== submenu && !otherSubmenu.classList.contains('hidden')) {
				const otherArrow = document.querySelector(`[data-target="${otherSubmenu.id}"] ${this.CONFIG.SELECTORS.arrow}`)
				this.swapClasses(otherSubmenu, STATES.submenu.open, STATES.submenu.closed)
				if (otherArrow) {
					this.swapClasses(otherArrow, STATES.arrow.open, STATES.arrow.closed)
				}
			}
		})

		// Toggle current submenu
		if (isOpen) {
			this.swapClasses(submenu, STATES.submenu.open, STATES.submenu.closed)
			if (arrow) this.swapClasses(arrow, STATES.arrow.open, STATES.arrow.closed)
		} else {
			this.swapClasses(submenu, STATES.submenu.closed, STATES.submenu.open)
			if (arrow) this.swapClasses(arrow, STATES.arrow.closed, STATES.arrow.open)
		}
	}

	toggle() {
		if (this.isOpen()) {
			this.close()
		} else {
			this.open()
		}
	}

	open() {
		const { STATES } = this.CONFIG
		this.swapClasses(this.elements.menu, STATES.menu.closed, STATES.menu.open)
		this.elements.button?.setAttribute('aria-expanded', 'true')
		this.elements.menu?.setAttribute('open', 'true')

		// Animate burger to X
		this.animateBurgerToX()

		// Prevent body scroll
		document.body.classList.add('overflow-hidden', 'lg:overflow-auto')
	}

	close() {
		const { STATES } = this.CONFIG
		this.swapClasses(this.elements.menu, STATES.menu.open, STATES.menu.closed)
		this.elements.button?.setAttribute('aria-expanded', 'false')
		this.elements.menu?.removeAttribute('open')

		// Animate X back to burger
		this.animateXToBurger()

		// Restore body scroll
		document.body.classList.remove('overflow-hidden')
	}

	isOpen() {
		return !this.elements.menu.classList.contains('hidden')
	}

	/**
	 * Animate burger menu icon to X
	 */
	animateBurgerToX() {
		const [line1, line2, line3] = this.elements.burgerLines
		if (!line1 || !line2 || !line3) return

		// Top line: rotate 45deg and move to center
		line1.style.transform = 'translateY(6px) rotate(45deg) scale(1.2)'

		// Middle line: fade out
		line2.style.opacity = '0'

		// Bottom line: rotate -45deg and move to center
		line3.style.transform = 'translateY(-6px) rotate(-45deg) scale(1.2)'
	}

	/**
	 * Animate X back to burger menu icon
	 */
	animateXToBurger() {
		const [line1, line2, line3] = this.elements.burgerLines
		if (!line1 || !line2 || !line3) return

		// Reset all transformations
		line1.style.transform = 'none'
		line2.style.opacity = '1'
		line3.style.transform = 'none'
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new MobileMenu())
} else {
	new MobileMenu()
}

// Export for use
window.MobileMenu = MobileMenu
